import { useState, useEffect, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import type { Editor as TiptapEditor } from '@tiptap/core';

import {
  Flex,
  Icon,
  useArrows,
  useI18n,
  defaultThemeProp,
  registerIcon,
  useOS,
  navigatorIsAvailable,
  useTestIds,
  withTestIds
} from '@pega/cosmos-react-core';
import type { AIAction, TestIdProp, TranslationPack } from '@pega/cosmos-react-core';
import type { TranslationFunction } from '@pega/cosmos-react-core/lib/i18n/translate';
import * as listIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/list.icon';
import * as listNumberIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/list-number.icon';
import * as indentIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/indent.icon';
import * as unindentIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/unindent.icon';

import ToolbarButton from '../../RichTextEditor/Toolbar/ToolbarButton';
import type { StyleType, Features } from '../../RichTextEditor/Toolbar/Toolbar.types';
import { getKeyCommand } from '../../RichTextEditor/Toolbar/utils';
import type {
  CustomAction,
  LinkCustomSourceConfig,
  ImageCustomSourceConfig
} from '../Editor.types';

import AIRewriteButton from './AIRewriteButton';
import ImageButton from './ImageButton';
import TextSelect from './TextSelect';
import AnchorButton from './AnchorButton';
import FontFamilySelect from './FontFamilySelect';
import FontSizeSelect from './FontSizeSelect';
import ColorPickerButton from './ColorPickerButton';
import TableButton from './TableButton';
import SourceCodeButton from './SourceCodeButton';
import AlignmentSelect from './AlignmentSelect';
import { getToolbarTestIds } from './Toolbar.test-ids';
import type { elements } from './Toolbar.test-ids';

registerIcon(listIcon, listNumberIcon, indentIcon, unindentIcon);

const StyledToolbar = styled.div<{ sticky?: boolean }>(({ sticky, theme }) => {
  return css`
    background-color: ${theme.components['form-control']['background-color']};
    border-end-start-radius: inherit;
    border-end-end-radius: inherit;
    overflow: auto;
    padding: calc(0.5 * ${theme.base.spacing});
    ${sticky &&
    css`
      position: sticky;
      bottom: 0;
    `}

    &:has(:focus-visible) {
      box-shadow: ${theme.base.shadow['focus-group']};
    }
  `;
});

const ToolbarSeparator = styled.div(({ theme }) => {
  return css`
    width: calc(0.125 * ${theme.base.spacing});
    height: calc(3 * ${theme.base.spacing});
    background-color: ${theme.base.palette['border-line']};
    margin-block: calc(0.5 * ${theme.base.spacing});
    margin-inline: calc(0.25 * ${theme.base.spacing});
  `;
});

ToolbarSeparator.defaultProps = defaultThemeProp;

StyledToolbar.defaultProps = defaultThemeProp;

const SubscriptText = styled.span(({ theme }) => {
  return css`
    display: inline-flex;
    align-items: flex-end;
    span {
      font-size: calc(0.65 * ${theme.base['font-size']});
      margin-bottom: calc(-0.25 * ${theme.base.spacing});
    }
  `;
});

SubscriptText.defaultProps = defaultThemeProp;

const SuperscriptText = styled.span(({ theme }) => {
  return css`
    display: inline-flex;
    align-items: flex-start;
    span {
      font-size: calc(0.65 * ${theme.base['font-size']});
      margin-top: calc(-0.25 * ${theme.base.spacing});
    }
  `;
});

SuperscriptText.defaultProps = defaultThemeProp;

export interface ToolbarProps extends TestIdProp<typeof elements> {
  features: Features[];
  sticky?: boolean;
  editor: TiptapEditor;
  customActions?: CustomAction[];
  imageInsertionMode: 'file' | 'url' | 'all';
  onRewriteClick?: (
    originalText: string,
    rewriteAction: AIAction,
    additionalInstructions: string,
    onSuccess: (rewrittenText: string) => void
  ) => void;
  linkCustomSource?: LinkCustomSourceConfig;
  imageCustomSource?: ImageCustomSourceConfig;
  renderSuggestionEditor?: (props: {
    defaultValue?: string;
    disabled: boolean;
    readOnly: boolean;
    onGetContent: (getContent: () => string) => void;
    onContentChange: (hasContent: boolean) => void;
  }) => ReactNode;
  sourceMode?: boolean;
  onSourceModeToggle?: (isSourceMode: boolean) => void;
}

type StyleButtonType = {
  'data-testid'?: string;
  style: StyleType;
  text: string;
  icon?: string;
  label?: string;
  format: string;
  element?: string;
  tooltip: string;
};

const isMobile = navigatorIsAvailable && navigator.userAgent.includes('Mobile');

// Individual inline style button definitions - keyed by format for safe lookup
function getInlineStyleButtons(
  t: TranslationFunction<TranslationPack>,
  osx: boolean,
  testIds: Pick<Partial<ReturnType<typeof getToolbarTestIds>>, 'bold' | 'italic' | 'strikeThrough'>
): { bold: StyleButtonType; italic: StyleButtonType; strikethrough: StyleButtonType } {
  return {
    bold: {
      'data-testid': testIds.bold,
      style: 'BOLD',
      text: 'B',
      format: 'Bold',
      label: t('rte_bold'),
      element: 'strong',
      tooltip: isMobile
        ? `${t('rte_bold')}`
        : getKeyCommand(osx, ({ ctrl, shift }) => `${t('rte_bold')} (${ctrl}B, ${ctrl}${shift}B)`)
    },
    italic: {
      'data-testid': testIds.italic,
      style: 'ITALIC',
      text: 'I',
      format: 'Italic',
      label: t('rte_italic'),
      element: 'i',
      tooltip: isMobile
        ? `${t('rte_italic')}`
        : getKeyCommand(osx, ({ ctrl, shift }) => `${t('rte_italic')} (${ctrl}I, ${ctrl}${shift}I)`)
    },
    strikethrough: {
      'data-testid': testIds.strikeThrough,
      style: 'STRIKE-THROUGH',
      text: 'S',
      format: 'Strikethrough',
      element: 's',
      label: t('rte_strike_through'),
      tooltip: isMobile
        ? `${t('rte_strike_through')}`
        : getKeyCommand(osx, ({ ctrl, shift }) => `${t('rte_strike_through')} (${ctrl}${shift}X)`)
    }
  };
}

// Render a single inline style button
function renderInlineStyleButton(
  editor: TiptapEditor,
  button: StyleButtonType,
  activeStyles: { [key: string]: boolean }
) {
  const { format, style, text, label, tooltip, 'data-testid': dataTestId } = button;
  return (
    <ToolbarButton
      data-testid={dataTestId}
      key={style}
      onMouseDown={e => {
        e.preventDefault();
        const chain = editor.chain().focus();
        if (format === 'Bold') {
          chain.toggleBold().run();
        } else if (format === 'Italic') {
          chain.toggleItalic().run();
        } else if (format === 'Strikethrough') {
          chain.toggleStrike().run();
        }
      }}
      styleType={style}
      active={activeStyles[format.toLowerCase()]}
      tooltip={tooltip}
      label={label}
    >
      {text}
    </ToolbarButton>
  );
}

// Takes the features array and transforms it into a map for faster lookup
function createActiveFeaturesMap(features: Features[]) {
  return features.reduce(
    (acc, feature) => {
      return { ...acc, [feature]: true };
    },
    {} as { [F in Features]: boolean }
  );
}

const Toolbar = ({
  testId,
  features,
  sticky,
  editor,
  customActions,
  imageInsertionMode,
  onRewriteClick,
  linkCustomSource,
  imageCustomSource,
  renderSuggestionEditor,
  sourceMode,
  onSourceModeToggle,
  ...restProps
}: ToolbarProps) => {
  const t = useI18n();
  const { macintosh: osx } = useOS();
  const toolbarRef = useRef<HTMLElement>(null);
  const testIds = useTestIds(testId, getToolbarTestIds);

  useArrows(toolbarRef, {
    selector: '[data-toolbar-item="true"]',
    dir: 'left-right'
  });

  const [activeFeatures, setActiveFeatures] = useState(() => createActiveFeaturesMap(features));
  const [activeStyles, setActiveStyles] = useState(() => ({
    bold: false,
    italic: false,
    strikethrough: false,
    underline: false,
    subscript: false,
    superscript: false,
    unorderedList: false,
    orderedList: false,
    alignLeft: false,
    alignCenter: false,
    alignRight: false
  }));

  const getIndentationToolTip = (type: 'indent' | 'unindent') =>
    getKeyCommand(osx, ({ shift, alt }) =>
      type === 'indent' ? `${t('rte_indent')} (${alt}M)` : `${t('rte_unindent')} (${alt}${shift}M)`
    );
  const tooltipMobile = (type: 'indent' | 'unindent') =>
    type === 'indent' ? `${t('rte_indent')}` : `${t('rte_unindent')}`;

  useEffect(() => {
    setActiveFeatures(createActiveFeaturesMap(features));
  }, [features]);

  const getActiveStyles = useCallback(() => {
    if (!editor) {
      setActiveStyles({
        bold: false,
        italic: false,
        strikethrough: false,
        underline: false,
        subscript: false,
        superscript: false,
        unorderedList: false,
        orderedList: false,
        alignLeft: false,
        alignCenter: false,
        alignRight: false
      });
      return;
    }
    // Tiptap API only
    setActiveStyles({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      strikethrough: editor.isActive('strike'),
      underline: editor.isActive('underline'),
      subscript: editor.isActive('subscript'),
      superscript: editor.isActive('superscript'),
      unorderedList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      alignLeft: editor.isActive({ textAlign: 'left' }),
      alignCenter: editor.isActive({ textAlign: 'center' }),
      alignRight: editor.isActive({ textAlign: 'right' })
    });
  }, [editor, setActiveStyles]);

  useEffect(() => {
    getActiveStyles();
    editor.on('selectionUpdate', getActiveStyles);
    editor.on('transaction', getActiveStyles);
    return () => {
      editor.off('selectionUpdate', getActiveStyles);
      editor.off('transaction', getActiveStyles);
    };
  }, [editor, getActiveStyles]);

  return (
    <Flex
      data-testid={testIds.toolbar}
      {...restProps}
      as={StyledToolbar}
      sticky={sticky}
      container={{ justify: 'between' }}
      ref={toolbarRef}
    >
      <Flex container={{ alignItems: 'center', gap: 0 }}>
        {/* Text styling group - TextSelect only when headers feature is enabled */}
        {activeFeatures.headers && (
          <TextSelect data-testid={testIds.textStyles} osx={osx} editor={editor} />
        )}
        {(() => {
          const buttons = getInlineStyleButtons(t, osx, testIds);
          // 'inline-styling' enables all three; individual features enable specific ones
          if (activeFeatures['inline-styling']) {
            return Object.values(buttons).map(btn =>
              renderInlineStyleButton(editor, btn, activeStyles)
            );
          }
          return (
            <>
              {activeFeatures.bold && renderInlineStyleButton(editor, buttons.bold, activeStyles)}
              {activeFeatures.italic &&
                renderInlineStyleButton(editor, buttons.italic, activeStyles)}
              {activeFeatures.strikethrough &&
                renderInlineStyleButton(editor, buttons.strikethrough, activeStyles)}
            </>
          );
        })()}
        {activeFeatures.underline && (
          <ToolbarButton
            data-testid={testIds.underline}
            styleType='UNDERLINE'
            onMouseDown={e => {
              e.preventDefault();
              editor.chain().focus().toggleUnderline().run();
            }}
            active={activeStyles.underline}
            tooltip={
              isMobile
                ? t('rte_underline')
                : getKeyCommand(osx, ({ ctrl }) => `${t('rte_underline')} (${ctrl}U)`)
            }
            label={t('rte_underline')}
          >
            U
          </ToolbarButton>
        )}
        {activeFeatures.subscript && (
          <ToolbarButton
            data-testid={testIds.subscript}
            styleType='SUBSCRIPT'
            onMouseDown={e => {
              e.preventDefault();
              // Turn off superscript first if it's active, then toggle subscript
              if (editor.isActive('superscript')) {
                editor.chain().focus().unsetSuperscript().toggleSubscript().run();
              } else {
                editor.chain().focus().toggleSubscript().run();
              }
            }}
            active={activeStyles.subscript}
            tooltip={t('rte_subscript')}
            label={t('rte_subscript')}
          >
            <SubscriptText>
              X<span>2</span>
            </SubscriptText>
          </ToolbarButton>
        )}
        {activeFeatures.superscript && (
          <ToolbarButton
            data-testid={testIds.superscript}
            styleType='SUPERSCRIPT'
            onMouseDown={e => {
              e.preventDefault();
              // Turn off subscript first if it's active, then toggle superscript
              if (editor.isActive('subscript')) {
                editor.chain().focus().unsetSubscript().toggleSuperscript().run();
              } else {
                editor.chain().focus().toggleSuperscript().run();
              }
            }}
            active={activeStyles.superscript}
            tooltip={t('rte_superscript')}
            label={t('rte_superscript')}
          >
            <SuperscriptText>
              X<span>2</span>
            </SuperscriptText>
          </ToolbarButton>
        )}
        {/* Separator after text styling */}
        {(activeFeatures['inline-styling'] ||
          activeFeatures.bold ||
          activeFeatures.italic ||
          activeFeatures.strikethrough ||
          activeFeatures.underline ||
          activeFeatures.subscript ||
          activeFeatures.superscript) &&
          (activeFeatures.alignment || activeFeatures.lists || activeFeatures.indentation) && (
            <ToolbarSeparator />
          )}
        {/* Layout group: alignment, lists, indentation */}
        {activeFeatures.alignment && (
          <AlignmentSelect data-testid={testIds.alignment} editor={editor} />
        )}
        {activeFeatures.lists && (
          <>
            <ToolbarButton
              data-testid={testIds.bulletedList}
              onMouseDown={e => {
                e.preventDefault();
                editor.chain().focus().toggleBulletList().run();
              }}
              active={activeStyles.unorderedList}
              tooltip={
                isMobile
                  ? `${t('rte_bulleted_list')}`
                  : getKeyCommand(
                      osx,
                      ({ ctrl, shift }) => `${t('rte_bulleted_list')} (${ctrl}${shift}L)`
                    )
              }
              label={t('rte_bulleted_list')}
            >
              <Icon name='list' />
            </ToolbarButton>
            <ToolbarButton
              data-testid={testIds.numberedList}
              onMouseDown={e => {
                e.preventDefault();
                editor.chain().focus().toggleOrderedList().run();
              }}
              active={activeStyles.orderedList}
              tooltip={t('rte_numbered_list')}
              label={t('rte_numbered_list')}
            >
              <Icon name='list-number' />
            </ToolbarButton>
          </>
        )}
        {activeFeatures.indentation && (
          <>
            <ToolbarButton
              data-testid={testIds.indent}
              onMouseDown={e => {
                e.preventDefault();
                // Try text indent first, fallback to list indent
                const textIndented = editor.chain().focus().indent().run();
                if (!textIndented) {
                  editor.chain().focus().sinkListItem('listItem').run();
                }
              }}
              label={t('rte_indent')}
              tooltip={isMobile ? tooltipMobile('indent') : getIndentationToolTip('indent')}
            >
              <Icon name='indent' />
            </ToolbarButton>
            <ToolbarButton
              data-testid={testIds.unindent}
              onMouseDown={e => {
                e.preventDefault();
                // Try text outdent first, fallback to list outdent
                const textOutdented = editor.chain().focus().outdent().run();
                if (!textOutdented) {
                  editor.chain().focus().liftListItem('listItem').run();
                }
              }}
              label={t('rte_unindent')}
              tooltip={isMobile ? tooltipMobile('unindent') : getIndentationToolTip('unindent')}
            >
              <Icon name='unindent' />
            </ToolbarButton>
          </>
        )}
        {/* Separator after layout group */}
        {(activeFeatures.alignment || activeFeatures.lists || activeFeatures.indentation) &&
          (activeFeatures['font-size'] ||
            activeFeatures['font-family'] ||
            activeFeatures['text-color'] ||
            activeFeatures['background-color']) && <ToolbarSeparator />}
        {/* Design group: font size, font family, text color, background color */}
        {activeFeatures['font-size'] && (
          <FontSizeSelect data-testid={testIds.fontSize} editor={editor} />
        )}
        {activeFeatures['font-family'] && (
          <FontFamilySelect data-testid={testIds.fontFamily} editor={editor} />
        )}
        {activeFeatures['text-color'] && (
          <ColorPickerButton data-testid={testIds.textColor} editor={editor} type='text' />
        )}
        {activeFeatures['background-color'] && (
          <ColorPickerButton
            data-testid={testIds.backgroundColor}
            editor={editor}
            type='highlight'
          />
        )}
        {/* Separator before source-code */}
        {(activeFeatures['font-size'] ||
          activeFeatures['font-family'] ||
          activeFeatures['text-color'] ||
          activeFeatures['background-color']) &&
          activeFeatures['source-code'] && <ToolbarSeparator />}
        {activeFeatures['source-code'] && (
          <SourceCodeButton
            data-testid={testIds.sourceCode}
            active={sourceMode}
            onToggle={onSourceModeToggle}
          />
        )}
      </Flex>
      <Flex container={{ alignItems: 'center', gap: 0 }}>
        {customActions?.map(
          ({
            text,
            icon,
            active,
            onMouseDown,
            // Avoid spreading shortcut to DOM attribute
            shortcut,
            ...rest
          }) => {
            return (
              <ToolbarButton
                {...rest}
                onMouseDown={e => {
                  e.preventDefault();
                  onMouseDown(e);
                }}
                tooltip={text}
                label={text}
                key={icon}
                active={active?.(editor) || false}
              >
                <Icon name={icon} />
              </ToolbarButton>
            );
          }
        )}
        {/* Insert group: links, images, source-code, tables */}
        {activeFeatures.links && (
          <AnchorButton
            data-testid={testIds.link}
            editor={editor}
            osx={osx}
            linkCustomSource={linkCustomSource}
          />
        )}
        {activeFeatures.images && (
          <ImageButton
            data-testid={testIds.image}
            editor={editor}
            imageInsertionMode={imageInsertionMode}
            imageCustomSource={imageCustomSource}
          />
        )}
        {activeFeatures.tables && <TableButton data-testid={testIds.table} editor={editor} />}
        {/* Separator before AI */}
        {(activeFeatures.links || activeFeatures.images || activeFeatures.tables) &&
          activeFeatures['ai-rewrite'] &&
          onRewriteClick && <ToolbarSeparator />}
        {activeFeatures['ai-rewrite'] && onRewriteClick && (
          <AIRewriteButton
            data-testid={testIds.aiRewrite}
            editor={editor}
            onRewriteClick={onRewriteClick}
            renderSuggestionEditor={renderSuggestionEditor}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default withTestIds(Toolbar, getToolbarTestIds);
